import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./installation.module.scss";
import { setInstallation } from "../../../../../stores/slices/get-solar-slice";
import { RootState } from "../../../../../stores/store";
import { SmallCheckIcon } from "../../../../../assets/icons";
import { Button } from "../../../components";

interface InstallationProps {
	onCancel: () => void;
	onSave: () => void;
}

export const Installation: React.FC<InstallationProps> = ({ onCancel, onSave }) => {
	const dispatch = useDispatch();
	const installation = useSelector((state: RootState) => state.getSolar.installation);
	const [canAuthorize, setCanAuthorize] = useState<boolean | null>(installation?.canAuthorize ?? null);
	const [electricitySupply, setElectricitySupply] = useState<string>(installation?.electricitySupply ?? "");
	const [installationAddress, setInstallationAddress] = useState(installation?.installationAddress ?? "");

	const isValid = canAuthorize !== null && electricitySupply.trim() !== "";

	const handleSave = () => {
		if (!isValid || canAuthorize === null) return;
		dispatch(
			setInstallation({
				canAuthorize,
				electricitySupply,
				installationAddress,
			})
		);
		onSave();
	};

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Installation</h2>
						<p className={styles.subtitle}>Provide us the following details for installation.</p>
					</div>
				</div>

				<div className={styles.formFields}>
					<div className={styles.formField}>
						<label className={styles.fieldLabel}>Can you authorize solar installation on your roof?*</label>
						<div className={styles.toggleRow}>
							<button className={`${styles.toggleOption} ${canAuthorize === true ? styles.toggleOptionSelected : ""}`} onClick={() => setCanAuthorize(true)}>
								{canAuthorize === true && (
									<span className={styles.checkIcon}>
										<SmallCheckIcon />
									</span>
								)}
								Yes, I can
							</button>
							<button className={`${styles.toggleOption} ${canAuthorize === false ? styles.toggleOptionSelected : ""}`} onClick={() => setCanAuthorize(false)}>
								{canAuthorize === false && (
									<span className={styles.checkIcon}>
										<SmallCheckIcon />
									</span>
								)}
								No, I can't
							</button>
						</div>
					</div>

					<div className={styles.formField}>
						<label className={styles.fieldLabel}>How much of electricity supply do you get in a day? *</label>
						<div className={styles.inputWithSuffix}>
							<input
								type="number"
								className={styles.textInput}
								placeholder="E.g 5"
								value={electricitySupply}
								onChange={e => {
									const val = e.target.value;
									if (val === "" || (Number(val) >= 0 && Number(val) <= 24)) {
										setElectricitySupply(val);
									}
								}}
								min={0}
								max={24}
							/>
							<span className={styles.inputSuffix}>Hours</span>
						</div>
					</div>

					<div className={styles.formField}>
						<label className={styles.fieldLabel}>Where would you like to install the solar? (eg installation address)</label>
						<input
							type="text"
							className={styles.textInput}
							placeholder='E.g "sample data"'
							value={installationAddress}
							onChange={e => setInstallationAddress(e.target.value)}
						/>
					</div>
				</div>

				<div className={styles.footer}>
					<div className={styles.footerInner}>
						<Button variant="secondary" onClick={onCancel}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSave} disabled={!isValid}>
							Save
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
